import { sendMessage } from '../api'
import {
  AgentAnswerPiece,
  DocumentsResponse,
  PacketType,
  StreamStopInfo,
  SubQueryPiece,
  SubQuestionDetail,
  SubQuestionPiece,
  SubQuestionSearchDoc,
} from '../types'

export class CurrentMessageFIFO {
  private stack: PacketType[] = []
  isComplete: boolean = false
  error: string | null = null

  push(packetBunch: PacketType) {
    this.stack.push(packetBunch)
  }

  nextPacket(): PacketType | undefined {
    return this.stack.shift()
  }

  isEmpty(): boolean {
    return this.stack.length === 0
  }
}

export const constructSubQuestions = (
  subQuestions: SubQuestionDetail[],
  newDetail:
    | SubQuestionPiece
    | SubQueryPiece
    | AgentAnswerPiece
    | SubQuestionSearchDoc
    | DocumentsResponse
    | StreamStopInfo
): SubQuestionDetail[] => {
  if (!newDetail) {
    return subQuestions
  }
  if (newDetail.level_question_num == 0) {
    return subQuestions
  }

  const updatedSubQuestions = [...subQuestions]

  if ('stop_reason' in newDetail) {
    const { level, level_question_num } = newDetail
    let subQuestion = updatedSubQuestions.find(
      (sq) => sq.level === level && sq.level_question_num === level_question_num
    )
    if (subQuestion) {
      if (newDetail.stream_type == 'sub_answer') {
        subQuestion.answer_streaming = false
      } else {
        subQuestion.is_complete = true
        subQuestion.is_stopped = true
      }
    }
  } else if ('top_documents' in newDetail) {
    const { level, level_question_num, top_documents } = newDetail
    let subQuestion = updatedSubQuestions.find(
      (sq) => sq.level === level && sq.level_question_num === level_question_num
    )
    if (!subQuestion) {
      subQuestion = {
        level: level ?? 0,
        level_question_num: level_question_num ?? 0,
        question: '',
        answer: '',
        sub_queries: [],
        context_docs: { top_documents },
        is_complete: false,
      }
    } else {
      subQuestion.context_docs = { top_documents }
    }
  } else if ('answer_piece' in newDetail) {
    // Handle AgentAnswerPiece
    const { level, level_question_num, answer_piece } = newDetail
    // Find or create the relevant SubQuestionDetail
    let subQuestion = updatedSubQuestions.find(
      (sq) => sq.level === level && sq.level_question_num === level_question_num
    )

    if (!subQuestion) {
      subQuestion = {
        level,
        level_question_num,
        question: '',
        answer: '',
        sub_queries: [],
        context_docs: undefined,
        is_complete: false,
      }
      updatedSubQuestions.push(subQuestion)
    }

    // Append to the answer
    subQuestion.answer += answer_piece
  } else if ('sub_question' in newDetail) {
    // Handle SubQuestionPiece
    const { level, level_question_num, sub_question } = newDetail

    // Find or create the relevant SubQuestionDetail
    let subQuestion = updatedSubQuestions.find(
      (sq) => sq.level === level && sq.level_question_num === level_question_num
    )

    if (!subQuestion) {
      subQuestion = {
        level,
        level_question_num,
        question: '',
        answer: '',
        sub_queries: [],
        context_docs: undefined,
        is_complete: false,
      }
      updatedSubQuestions.push(subQuestion)
    }

    // Append to the question
    subQuestion.question += sub_question
  } else if ('sub_query' in newDetail) {
    // Handle SubQueryPiece
    const { level, level_question_num, query_id, sub_query } = newDetail

    // Find the relevant SubQuestionDetail
    let subQuestion = updatedSubQuestions.find(
      (sq) => sq.level === level && sq.level_question_num === level_question_num
    )

    if (!subQuestion) {
      // If we receive a sub_query before its parent question, create a placeholder
      subQuestion = {
        level,
        level_question_num: level_question_num,
        question: '',
        answer: '',
        sub_queries: [],
        context_docs: undefined,
      }
      updatedSubQuestions.push(subQuestion)
    }

    // Find or create the relevant SubQueryDetail
    let subQuery = subQuestion.sub_queries?.find((sq) => sq.query_id === query_id)

    if (!subQuery) {
      subQuery = { query: '', query_id }
      subQuestion.sub_queries = [...(subQuestion.sub_queries || []), subQuery]
    }

    // Append to the query
    subQuery.query += sub_query
  }

  return updatedSubQuestions
}

export async function updateCurrentMessageFIFO(
  stack: CurrentMessageFIFO,
  params: { message: string; sessionId: string; parentMessageId?: number; signal?: AbortSignal }
) {
  try {
    for await (const packet of sendMessage(params)) {
      if (params.signal?.aborted) {
        throw new Error('AbortError')
      }
      stack.push(packet)
    }
  } catch (error: unknown) {
    if (error instanceof Error) {
      if (error.name === 'AbortError') {
        console.debug('Stream aborted')
      } else {
        stack.error = error.message
      }
    } else {
      stack.error = String(error)
    }
  } finally {
    stack.isComplete = true
  }
}
